import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileUp, Sparkles, BookMarked } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NONE = "__none__";

// Upload an old quote / computo / photo -> AI extracts editable quote data.
// Optionally match extracted items against a chosen price book (preziario).
export default function ImportQuoteDialog({ open, onClose, onExtracted }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [preziarioId, setPreziarioId] = useState(NONE);

  useEffect(() => {
    if (open) cachedGet("/preziari").then((r) => setBooks(r.data || [])).catch(() => {});
  }, [open]);

  const run = async () => {
    const files = fileRef.current?.files;
    if (!files || !files.length) { toast.error("Seleziona un PDF o una foto del preventivo/computo"); return; }
    const fd = new FormData();
    fd.append("file", files[0]);
    if (preziarioId && preziarioId !== NONE) fd.append("preziario_id", preziarioId);
    setLoading(true);
    try {
      const r = await api.post("/quotes/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const voci = r.data.voci || [];
      if (!voci.length) { toast.error("Nessuna voce riconosciuta nel documento. Prova con un file più leggibile."); return; }
      const withPrezzario = preziarioId && preziarioId !== NONE;
      toast.success(`${voci.length} voci estratte${withPrezzario ? " con prezzi dal preziario" : ""}. Controlla e modifica prima di salvare.`);
      onExtracted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Estrazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="import-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Importa preventivo o computo
          </DialogTitle>
          <DialogDescription className="sr-only">Carica un documento e l'assistente estrae le voci del preventivo, modificabili prima del salvataggio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Carica un <strong>vecchio preventivo</strong>, un <strong>computo</strong> o una <strong>foto</strong> (PDF, JPG, PNG).
            L'assistente leggerà il documento e creerà un preventivo che potrai <strong>modificare in ogni sua parte</strong> prima di salvarlo.
          </p>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5 text-primary" /> Preziario per i prezzi (opzionale)</Label>
            <Select value={preziarioId} onValueChange={setPreziarioId}>
              <SelectTrigger data-testid="import-preziario-select"><SelectValue placeholder="Nessuno — usa i prezzi del documento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Nessuno — usa i prezzi del documento</SelectItem>
                {books.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.nome} ({b.item_count} voci · +{b.markup}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Se scegli un preziario, i prezzi delle voci riconosciute vengono presi da lì (con l'aumento % applicato).</p>
          </div>

          <Input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="import-file-input" />
          <Button className="w-full" onClick={run} disabled={loading} data-testid="import-run-button">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Lettura in corso…</> : <><FileUp className="w-4 h-4 mr-1.5" /> Crea preventivo dal file</>}
          </Button>
          {loading && <p className="text-xs text-center text-muted-foreground">L'analisi può richiedere fino a un minuto per documenti lunghi.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
